import React, { useEffect, useState } from 'react'
import { type ISlot } from '../../../models/DistributionPlan'
import GenericModal from '../../../components/GenericModal'
import { Box, Tab, Typography } from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import SurveyDatesPanel from './SurveyDatesPanel'
import SyncRulesPanel from './SyncRulesPanel'
import { green, grey } from '../../../colors'
import { useQuery } from '@tanstack/react-query'
import { AutoImportManager } from '../../../APIManager'

interface SyncSectionModalProps {
  slot: ISlot
  courseSectionsModal: boolean
  setCourseSectionsModal: (arg: boolean) => void
}

const SyncSectionModal: React.FC<SyncSectionModalProps> = ({ slot, courseSectionsModal, setCourseSectionsModal }) => {
  const [tab, setTab] = useState<string>('1')
  const isAutoImportActive = slot.isCourseAttributeFilterActive || slot.isReportingHierarchyFilterActive || slot.isSyncAllSectionsForTermActive

  const handleChange = (event: React.SyntheticEvent, newValue: string): void => {
    setTab(newValue)
  }

  const handleCloseModal = (): void => {
    setCourseSectionsModal(false)
  }

  const setNextTab = (): void => {
    setTab('2')
  }

  const ModalTitle = (): JSX.Element => {
    return <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={2} >
      <Typography fontSize={'18px'} fontWeight={600}>Sync Course Sections</Typography>
      {
        tab === '2' &&
        <>
          <Box borderRadius={'100%'} bgcolor={isAutoImportActive ? green[200] : grey[150]} width={'10px'} height={'10px'}></Box>
          <Typography fontSize={'14px'} color={isAutoImportActive ? green[200] : grey[150]} >{isAutoImportActive ? 'Sync On' : 'Sync Off'}</Typography>
        </>
      }
    </Box>
  }

  const { data: surveyDatesList, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['dates', slot.slotId],
    retry: false,
    queryFn: async () => await AutoImportManager.getSurveyDates(slot?.slotId)
  })

  useEffect(() => {
    if (courseSectionsModal) {
      void refetch()
    }
  }, [tab, refetch, courseSectionsModal])

  const isSyncRulesPanelDisabled = surveyDatesList?.length === 0

  return (
    <>

      <GenericModal
        isOpen={courseSectionsModal}
        title={<ModalTitle />}
        onClose={() => { setCourseSectionsModal(false) }}
        maxWidth='lg'
        action={false}
        showCloseIcon={false}
      >
        <>
          <Typography>Set up rules to automatically sync course sections with your survey project.</Typography>
          <Box mt={'20px'}>
            <TabContext value={tab} >
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={handleChange} aria-label="tab">
                  <Tab label="Survey Dates" value="1" />
                  <Tab label="Sync Rules" value="2" />
                </TabList>
              </Box>
              <TabPanel value="1">
                <SurveyDatesPanel handleCloseModal={handleCloseModal} slot={slot} setNextTab={setNextTab} surveyDatesList={surveyDatesList ?? []} loading={isLoading || isFetching} />
              </TabPanel>
              <TabPanel value="2" >
                <SyncRulesPanel handleCloseModal={handleCloseModal} slot={slot} />
              </TabPanel>
            </TabContext>
          </Box>
        </>
      </GenericModal>
    </>
  )
}

export default SyncSectionModal
